import React from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, Shield } from 'lucide-react';
import { formatDate, formatTime } from '../../../utils/dateUtils';

const ComplianceAuditPanel = ({ complianceAudit }) => {
  const totals = complianceAudit?.totals_by_type || [];
  const recent = complianceAudit?.recent || [];
  const total = complianceAudit?.total || 0;

  return (
    <div className="bg-white border rounded-lg p-3 sm:p-4">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            Compliance audit stream
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Immutable record of sensitive admin actions — payouts, date changes, vendor verification.
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Audit events</p>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">{total}</p>
        </div>
      </div>

      {total === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
          <FileCheck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No compliance events for this period. Approve a vendor, record a payout, or resolve a date-change request.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By event type</h3>
            <div className="space-y-2">
              {totals.map((row) => (
                <div key={row.event_type} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{row.label}</span>
                  <span className="font-semibold text-gray-900 tabular-nums">{row.count}</span>
                </div>
              ))}
            </div>
          </div>

          {recent.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent events</h3>
              <div className="space-y-3">
                {recent.map((event) => (
                  <div key={event.id} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                    <div className="flex justify-between gap-2">
                      <span className="font-medium text-gray-900">{event.label}</span>
                      <span className="text-gray-500 shrink-0 tabular-nums">
                        {event.created_at
                          ? `${formatDate(event.created_at)} ${formatTime(event.created_at)}`
                          : '—'}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-0.5">
                      {event.actor?.name || 'System'}
                      {event.aggregate_id ? (
                        <span className="text-gray-400">
                          {' '}
                          · {event.aggregate_type} #{String(event.aggregate_id).slice(0, 8)}
                        </span>
                      ) : null}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-gray-100">
        <Link
          to="/admin/compliance-audit"
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          View full compliance audit log →
        </Link>
      </div>
    </div>
  );
};

export default ComplianceAuditPanel;
